import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  CircularProgress,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Delete as DeleteIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const GenerateImage = () => {
  const { loraId } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedImages, setGeneratedImages] = useState([]);
  const [lora, setLora] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user_email = localStorage.getItem('user_email');
    
    if (!token || !user_email) {
      navigate('/login');
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['x-email'] = user_email;
    
    fetchLora();
    fetchGeneratedImages();
  }, [loraId, navigate]);

  const fetchLora = async () => {
    try {
      const response = await axios.get(`/api/loras/${loraId}`);
      setLora(response.data);
      if (response.data.status !== 'completed') {
        setError('This LoRA model is not ready for image generation');
      }
    } catch (error) {
      console.error('Error fetching LoRA:', error);
      setError('Failed to fetch LoRA details');
      if (error.response?.status === 404) {
        navigate('/dashboard');
      }
    }
  };

  const fetchGeneratedImages = async () => {
    try {
      const response = await axios.get(`/api/loras/${loraId}/generated-images`);
      setGeneratedImages(response.data);
    } catch (error) {
      console.error('Error fetching generated images:', error);
      setError('Failed to fetch generated images');
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await axios.post(`/api/loras/${loraId}/generate`, {
        prompt: prompt.trim()
      });
      
      // Show preview dialog instead of immediately saving
      setPreviewImage({
        url: response.data.image_url,
        prompt: prompt.trim()
      });
      setPreviewDialog(true);
      setPrompt('');
    } catch (error) {
      console.error('Error generating image:', error);
      setError(error.response?.data?.error || 'Failed to generate image');
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveImage = async () => {
    try {
      const response = await axios.post(`/api/loras/${loraId}/save-generated`, {
        image_url: previewImage.url,
        prompt: previewImage.prompt
      });
      
      setGeneratedImages(prev => [response.data.image, ...prev]);
      setPreviewDialog(false);
      setPreviewImage(null);
    } catch (error) {
      console.error('Error saving image:', error);
      setError('Failed to save the generated image');
    }
  };

  const handleRejectImage = () => {
    setPreviewDialog(false);
    setPreviewImage(null);
  };

  const handleDelete = async (imageId) => {
    try {
      await axios.delete(`/api/loras/${loraId}/generated-images/${imageId}`);
      setGeneratedImages(prev => prev.filter(img => img._id !== imageId));
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Failed to delete image');
    }
  };

  const downloadImage = (url) => {
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = url;
    // Extract filename from URL or use timestamp
    const filename = url.split('/').pop() || `generated-image-${Date.now()}.jpg`;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>

      <Typography variant="h4" component="h1" sx={{ mb: 4, color: '#369FA1' }}>
        Generate Images with {lora?.name}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          label="Enter your prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          multiline
          rows={3}
          placeholder={`Example: a photo of ${lora?.name} in a beautiful garden`}
          disabled={generating}
        />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1 }}>
          Tip: Include "{lora?.name}" in your prompt to get better results with your trained model.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={generating || !prompt.trim()}
          sx={{
            mt: 2,
            bgcolor: '#369FA1',
            '&:hover': { bgcolor: '#2B8183' }
          }}
        >
          {generating ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
              Generating...
            </>
          ) : (
            'Generate Image'
          )}
        </Button>
      </Box>

      <Typography variant="h5" sx={{ mb: 2 }}>
        Generated Images
      </Typography>

      <Grid container spacing={3}>
        {generatedImages.map((image) => (
          <Grid item xs={12} sm={6} md={4} key={image._id}>
            <Card>
              <CardMedia
                component="img"
                image={image.url}
                alt={`Generated with prompt: ${image.prompt}`}
                sx={{ height: 300, objectFit: 'cover' }}
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {image.prompt}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <IconButton
                    onClick={() => downloadImage(image.url)}
                    sx={{ color: '#369FA1' }}
                  >
                    <CloudDownloadIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setConfirmDelete(image._id)}
                    sx={{ color: '#d32f2f' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={previewDialog}
        onClose={handleRejectImage}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review Generated Image</DialogTitle>
        <DialogContent>
          {previewImage && (
            <>
              <Box sx={{ mt: 2, mb: 2 }}>
                <img
                  src={previewImage.url}
                  alt="Generated preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px'
                  }}
                />
              </Box>
              <Typography variant="body1" color="textSecondary">
                Prompt: {previewImage.prompt}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectImage} color="error">
            Reject
          </Button>
          <Button
            onClick={handleApproveImage}
            variant="contained"
            sx={{
              bgcolor: '#369FA1',
              '&:hover': { bgcolor: '#2B8183' }
            }}
          >
            Approve & Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this generated image?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            onClick={() => handleDelete(confirmDelete)}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GenerateImage; 